import React, { useEffect, useMemo, useState } from "react";
import {
  parseGrid, guessMapping, mapRowsToStudents, FIELD_OPTIONS,
} from "../hooks/importHelpers";
import "./AddStudent.css";

// Shared, mapping-based bulk importer.
//  - mode="class": rows go into the page's class (teacher Add-Students). onImport(students).
//  - mode="directory": school-wide (admin). Maps a Homeroom/Class column; class names
//    that don't exist yet are CREATED, owned by a teacher resolved from a Teacher-email
//    column (or a per-class dropdown defaulting to the admin). onImport({ students, newClasses }).
//  - mode="directory" + assignToSelf: teacher version — same class-creating import, but
//    every new class is owned by the importer (no Teacher-email column, no teacher picker).
export default function BulkImport({
  mode = "class",
  classOptions = [],     // existing classes: [{ id, className }]
  teacherOptions = [],   // school users: [{ uid, email, name }]
  defaultTeacherId = "", // fallback owner for new classes (the admin, or self)
  assignToSelf = false,  // directory import where all new classes go to the importer
  onImport,
}) {
  const [inputMode, setInputMode] = useState("paste"); // paste | file
  const [text, setText] = useState("");
  const [mapping, setMapping] = useState([]);
  const [assignOverride, setAssignOverride] = useState({}); // newClassKey -> teacherId
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState(null);

  const isDirectory = mode === "directory";
  const parsed = useMemo(() => parseGrid(text), [text]);
  const colCount = parsed.rows[0]?.length || parsed.headerLabels?.length || 0;

  useEffect(() => {
    setMapping(colCount > 0 ? guessMapping(parsed.headerLabels, parsed.rows) : []);
    setAssignOverride({});
    setStatus(null);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide homeroom/teacher-email mapping in class mode (rows go into the current class).
  // In the self-assign teacher directory import there's no Teacher-email column either.
  const fieldOptions = isDirectory
    ? (assignToSelf
        ? FIELD_OPTIONS
            .filter((o) => o.value !== "teacherEmail")
            .map((o) => (o.value === "homeroom" ? { ...o, label: "Class" } : o))
        : FIELD_OPTIONS)
    : FIELD_OPTIONS.filter((o) => o.value !== "homeroom" && o.value !== "teacherEmail");

  const teacherName = (uid) => {
    const t = teacherOptions.find((o) => o.uid === uid);
    return t ? (t.name || t.email) : "—";
  };

  // Mapped + (directory) homeroom-matched students.
  const students = useMemo(() => {
    if (!mapping.length) return [];
    const mapped = mapRowsToStudents(parsed.rows, mapping);
    if (!isDirectory) return mapped.map((s) => ({ ...s, rowValid: s.isValid }));
    return mapped.map((s) => {
      const key = (s.homeroomRaw || "").trim().toLowerCase();
      const existing = key
        ? classOptions.find((c) => (c.className || "").trim().toLowerCase() === key)
        : null;
      return {
        ...s,
        homeroomKey: key,
        homeroomClassId: existing?.id || null, // null + key set => will be created
        rowValid: s.isValid, // homeroom always resolves now (match or create)
      };
    });
  }, [mapping, parsed, isDirectory, classOptions]);

  // Distinct homeroom names with no existing match → classes to create.
  const newClasses = useMemo(() => {
    if (!isDirectory) return [];
    const map = new Map();
    students.forEach((s) => {
      if (!s.isValid || !s.homeroomKey || s.homeroomClassId) return;
      if (!map.has(s.homeroomKey)) map.set(s.homeroomKey, { name: s.homeroomRaw.trim(), key: s.homeroomKey, emails: new Set(), grades: [] });
      const e = map.get(s.homeroomKey);
      if (s.teacherEmailRaw) e.emails.add(s.teacherEmailRaw.trim().toLowerCase());
      if (s.grade) e.grades.push(s.grade);
    });
    return [...map.values()].map((c) => {
      let suggested = defaultTeacherId;
      let emailUnresolved = false;
      if (c.emails.size) {
        const hit = [...c.emails]
          .map((em) => teacherOptions.find((t) => (t.email || "").toLowerCase() === em))
          .find(Boolean);
        if (hit) suggested = hit.uid;
        else emailUnresolved = true;
      }
      const uniqueGrades = [...new Set(c.grades)];
      return { name: c.name, key: c.key, suggestedTeacherId: suggested, emailUnresolved, gradeLevel: uniqueGrades.length === 1 ? uniqueGrades[0] : "" };
    });
  }, [students, isDirectory, teacherOptions, defaultTeacherId]);

  const teacherFor = (key) =>
    assignOverride[key] ?? newClasses.find((c) => c.key === key)?.suggestedTeacherId ?? defaultTeacherId;

  const validStudents = students.filter((s) => s.rowValid);

  const rowStatus = (s) => {
    if (!s) return { ok: false, msg: "" };
    if (!s.studentName) return { ok: false, msg: "Missing name" };
    // ID + grade are optional now — a nameless row is the only hard failure; a
    // non-blank unrecognized grade still flags via isValid.
    if (!s.isValid) return { ok: false, msg: "Bad grade" };
    if (isDirectory && s.homeroomKey) {
      const existing = classOptions.find((c) => (c.className || "").trim().toLowerCase() === s.homeroomKey);
      if (existing) return { ok: true, msg: `→ ${existing.className}` };
      return { ok: true, msg: assignToSelf ? "New class" : `New class → ${teacherName(teacherFor(s.homeroomKey))}` };
    }
    return { ok: true, msg: "Valid" };
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText(ev.target.result || "");
    reader.readAsText(file);
  };

  const setColumn = (i, value) => setMapping((prev) => prev.map((m, j) => (j === i ? value : m)));

  const handleImport = async () => {
    if (!validStudents.length) return;
    setImporting(true);
    setStatus(null);
    try {
      const payload = isDirectory
        ? { students: validStudents, newClasses: newClasses.map((c) => ({ name: c.name, teacherId: teacherFor(c.key), gradeLevel: c.gradeLevel })) }
        : validStudents;
      const count = await onImport(payload);
      const n = count ?? validStudents.length;
      setText("");
      setStatus({ type: "success", msg: `${n} student${n !== 1 ? "s" : ""} imported.` });
    } catch (err) {
      console.error("Bulk import error:", err);
      setStatus({ type: "error", msg: "Import failed. Please try again." });
    } finally {
      setImporting(false);
    }
  };

  const flagged = students.length - validStudents.length;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button type="button" onClick={() => setInputMode("paste")} className={inputMode === "paste" ? "" : "btn-secondary"}>
          Paste List
        </button>
        <button type="button" onClick={() => setInputMode("file")} className={inputMode === "file" ? "" : "btn-secondary"}>
          Upload CSV
        </button>
      </div>

      <p style={{ fontSize: 13, color: "var(--color-secondary)", marginBottom: 8 }}>
        Paste or upload any list — even just <strong>one name per line</strong> (IDs and grade are optional).
        With or without a header row; map each column below.{isDirectory ? (assignToSelf
          ? " Add a Class column — classes that don't exist yet are created and assigned to you."
          : " Add a Homeroom/Class column (and optionally a Teacher email) — classes that don't exist yet are created and assigned.") : ""}
      </p>

      {inputMode === "paste" ? (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={!isDirectory
            ? "Jane Smith\nCarlos Diaz\nAmir Khan\n\n…or paste columns: Last, First\tID\tGrade\nSmith, Jane\t12345\t3"
            : assignToSelf
            ? "Last, First\tID\tGrade\tClass\nSmith, Jane\t12345\tK\tRoom 12 - Morning"
            : "Last, First\tID\tGrade\tHomeroom\tTeacher email\nSmith, Jane\t12345\tK\tGrasshopper - Week 1\tms.lee@school.org"}
          style={{ height: 150, fontSize: 14, width: "100%" }}
        />
      ) : (
        <input type="file" accept=".csv,.tsv,.txt" onChange={handleFile} />
      )}

      {colCount > 0 && (
        <>
          <div style={{ overflowX: "auto", marginTop: 16 }}>
            <table className="table" style={{ minWidth: 520 }}>
              <thead>
                <tr>
                  {Array.from({ length: colCount }).map((_, i) => (
                    <th key={i} style={{ padding: 8 }}>
                      <select value={mapping[i] || "ignore"} onChange={(e) => setColumn(i, e.target.value)} style={{ width: "auto", minWidth: 110 }}>
                        {fieldOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </th>
                  ))}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, 100).map((row, ri) => {
                  const st = rowStatus(students[ri]);
                  return (
                    <tr key={ri}>
                      {Array.from({ length: colCount }).map((_, ci) => (
                        <td key={ci}>{row[ci] || ""}</td>
                      ))}
                      <td style={{ color: st.ok ? "var(--color-success)" : "var(--color-error)", whiteSpace: "nowrap", fontWeight: 600 }}>
                        {st.msg}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {parsed.rows.length > 100 && (
            <p style={{ fontSize: 12, color: "var(--color-secondary)", marginTop: 6 }}>
              Showing first 100 of {parsed.rows.length} rows.
            </p>
          )}

          {isDirectory && newClasses.length > 0 && (
            <div style={{ marginTop: 18, padding: "14px 16px", background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
              <h4 style={{ margin: "0 0 4px" }}>New classes to create ({newClasses.length})</h4>
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--color-secondary)" }}>
                {assignToSelf
                  ? "These class names don't exist yet — they'll be created and assigned to you."
                  : "These homeroom names don't exist yet. Pick the teacher who'll own each (auto-filled from the Teacher email column when it matches a staff member)."}
              </p>
              {newClasses.map((c) => (
                <div key={c.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, minWidth: 180 }}>
                    {c.name}{c.gradeLevel ? <span style={{ color: "var(--color-secondary)", fontWeight: 400 }}> · {c.gradeLevel}</span> : null}
                  </span>
                  {assignToSelf ? (
                    <span style={{ color: "var(--color-secondary)", fontSize: 13 }}>→ you</span>
                  ) : (
                    <>
                      <select value={teacherFor(c.key)} onChange={(e) => setAssignOverride((p) => ({ ...p, [c.key]: e.target.value }))} style={{ width: "auto", minWidth: 200 }}>
                        {teacherOptions.map((t) => (
                          <option key={t.uid} value={t.uid}>{t.name || t.email}</option>
                        ))}
                      </select>
                      {c.emailUnresolved && (
                        <span style={{ fontSize: 12, color: "var(--color-warning)" }}>email not found — assign manually</span>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14, flexWrap: "wrap" }}>
            <button type="button" onClick={handleImport} disabled={importing || !validStudents.length}>
              {importing ? "Importing…" : `Import ${validStudents.length} student${validStudents.length !== 1 ? "s" : ""}`}
            </button>
            {flagged > 0 && (
              <span style={{ fontSize: 13, color: "var(--color-error)" }}>
                {flagged} row{flagged !== 1 ? "s" : ""} need fixing (remap a column or correct the source).
              </span>
            )}
          </div>
        </>
      )}

      {status && <p className={`as-status ${status.type}`}>{status.msg}</p>}
    </div>
  );
}
